import * as moment from 'moment';
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import { RefTypeItem } from '@sd-portal/shared/ref-type-item';

import * as actions from './aircraft-event.actions';
import { auditLogAdapter } from '../shared-audit.adapters';
import {
  AircraftEventSkeleton,
  AircraftEventAudit,
  AircraftEventBusinessPersonAudit,
  ScheduledEventAudit
} from '../../+models/aircraft-event-audit.model';

// States
interface AircraftEventSkeletonState extends EntityState<AircraftEventSkeleton> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
}

interface AircraftEventAuditState extends EntityState<AircraftEventAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface AircraftEventBusinessPersonAuditState
  extends EntityState<AircraftEventBusinessPersonAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface ScheduledEventAuditState extends EntityState<ScheduledEventAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface AircraftEventTypeState extends EntityState<RefTypeItem> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
}

interface FlightBusinessPersonTypeState extends EntityState<RefTypeItem> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
}

function sortAircraftEvents(a: AircraftEventSkeleton, b: AircraftEventSkeleton) {
  const tailNumberCompareResult = a.tailNumber.localeCompare(b.tailNumber);
  if (tailNumberCompareResult !== 0) {
    return tailNumberCompareResult;
  }
  const aStartDateTimeUtc = moment(a.startDateTimeUtc);
  const bStartDateTimeUtc = moment(b.startDateTimeUtc);
  if (aStartDateTimeUtc.isBefore(bStartDateTimeUtc)) {
    return -1;
  } else if (aStartDateTimeUtc.isSame(bStartDateTimeUtc)) {
    return 0;
  } else {
    return 1;
  }
}

// Adapters
const aircraftEventSkeletonsAdapter = createEntityAdapter<AircraftEventSkeleton>({
  selectId: p => p.aircraftEventId,
  sortComparer: sortAircraftEvents
});

const aircraftEventTypeAdapter = createEntityAdapter<RefTypeItem>({
  selectId: p => p.id
});

const flightBusinessPersonTypeAdapter = createEntityAdapter<RefTypeItem>({
  selectId: p => p.id
});

// Entity Selectors
export const aircraftEventSkeletonSelectors = aircraftEventSkeletonsAdapter.getSelectors();
export const aircraftEventAuditSelectors = auditLogAdapter.getSelectors();
export const aircraftEventBusinessPersonAuditSelectors = auditLogAdapter.getSelectors();
export const scheduledEventAuditSelectors = auditLogAdapter.getSelectors();
export const aircraftEventTypeSelectors = aircraftEventTypeAdapter.getSelectors();
export const flightBusinessPersonTypeSelectors = flightBusinessPersonTypeAdapter.getSelectors();

// Initial States
const aircraftEventSkeletonsInitialState = aircraftEventSkeletonsAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false
});

const aircraftEventAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const aircraftEventBusinessPersonAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const scheduledEventAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const aircraftEventTypeInitialState = aircraftEventTypeAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false
});

const flightBusinessPersonTypeInitialState = flightBusinessPersonTypeAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false
});

export interface State {
  aircraftEventSkeletons: AircraftEventSkeletonState;
  aircraftEventAudit: AircraftEventAuditState;
  aircraftEventBusinessPersonAudit: AircraftEventBusinessPersonAuditState;
  scheduledEventAudit: ScheduledEventAuditState;
  aircraftEventTypes: AircraftEventTypeState;
  flightBusinessPersonTypes: FlightBusinessPersonTypeState;
}

const initialState: State = {
  aircraftEventSkeletons: aircraftEventSkeletonsInitialState,
  aircraftEventAudit: aircraftEventAuditInitialState,
  aircraftEventBusinessPersonAudit: aircraftEventBusinessPersonAuditInitialState,
  scheduledEventAudit: scheduledEventAuditInitialState,
  aircraftEventTypes: aircraftEventTypeInitialState,
  flightBusinessPersonTypes: flightBusinessPersonTypeInitialState
};

const aircraftEventReducer = createReducer<State>(
  initialState,
  on(actions.ResetAircraftEvent, () => initialState),

  on(actions.LoadAircraftEventSkeletons, state => ({
    ...state,

    aircraftEventSkeletons: {
      ...state.aircraftEventSkeletons,
      loading: true
    }
  })),

  on(actions.AircraftEventSkeletonsLoaded, (state, action) => ({
    ...state,

    aircraftEventSkeletons: {
      ...state.aircraftEventSkeletons,
      ...aircraftEventSkeletonsAdapter.setAll(action.payload, state.aircraftEventSkeletons),
      loading: false,
      loaded: true
    }
  })),

  on(actions.LoadAircraftEventAudit, (state, action) => ({
    ...state,

    aircraftEventAudit: {
      ...state.aircraftEventAudit,
      lastAuditLogId:
        state.aircraftEventAudit.selectedId === action.aircraftEventId
          ? state.aircraftEventAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.AircraftEventAuditLoaded, (state, action) => ({
    ...state,

    aircraftEventAudit: {
      ...state.aircraftEventAudit,
      ...(!state.aircraftEventAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.aircraftEventAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.aircraftEventAudit)),
      loading: false,
      loaded: true,
      selectedId: action.aircraftEventId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadAircraftEventBusinessPersonAudit, (state, action) => ({
    ...state,

    aircraftEventBusinessPersonAudit: {
      ...state.aircraftEventBusinessPersonAudit,
      lastAuditLogId:
        state.aircraftEventBusinessPersonAudit.selectedId === action.aircraftEventBusinessPersonId
          ? state.aircraftEventBusinessPersonAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.AircraftEventBusinessPersonAuditLoaded, (state, action) => ({
    ...state,

    aircraftEventBusinessPersonAudit: {
      ...state.aircraftEventBusinessPersonAudit,
      ...(!state.aircraftEventBusinessPersonAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.aircraftEventBusinessPersonAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.aircraftEventBusinessPersonAudit)),
      loading: false,
      loaded: true,
      selectedId: action.aircraftEventBusinessPersonId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadScheduledEventAudit, (state, action) => ({
    ...state,

    scheduledEventAudit: {
      ...state.scheduledEventAudit,
      lastAuditLogId:
        state.scheduledEventAudit.selectedId === action.scheduledEventId
          ? state.scheduledEventAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.ScheduledEventAuditLoaded, (state, action) => ({
    ...state,

    scheduledEventAudit: {
      ...state.scheduledEventAudit,
      ...(!state.scheduledEventAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.scheduledEventAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.scheduledEventAudit)),
      loading: false,
      loaded: true,
      selectedId: action.scheduledEventId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadAircraftEventTypes, state => ({
    ...state,

    aircraftEventTypes: {
      ...state.aircraftEventTypes,
      loading: true
    }
  })),

  on(actions.AircraftEventTypesLoaded, (state, action) => ({
    ...state,

    aircraftEventTypes: {
      ...state.aircraftEventTypes,
      ...aircraftEventTypeAdapter.setAll(action.payload, state.aircraftEventTypes),
      loading: false,
      loaded: true
    }
  })),

  on(actions.LoadFlightBusinessPersonTypes, state => ({
    ...state,

    flightBusinessPersonTypes: {
      ...state.flightBusinessPersonTypes,
      loading: true
    }
  })),

  on(actions.FlightBusinessPersonTypesLoaded, (state, action) => ({
    ...state,

    flightBusinessPersonTypes: {
      ...state.flightBusinessPersonTypes,
      ...flightBusinessPersonTypeAdapter.setAll(action.payload, state.flightBusinessPersonTypes),
      loading: false,
      loaded: true
    }
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return aircraftEventReducer(state, action);
}
